/**
 * التغذية الراجعة.
 *
 * ملاحظةٌ **على** سجل يكتبها زميل ليقرأها زميل، وتُردّ. وهي ليست نشاطًا وإن
 * تشابها في الظاهر:
 *
 *   النشاط شيءٌ **وقع** — مكالمة جرت، اجتماع عُقد، مهمة تُنجَز — له موعد
 *   ومسؤول وحالة، ويدخل في تقارير الأداء وفي «آخر تواصل».
 *
 *   والتغذية الراجعة **رأيٌ في** ما وقع: «العرض مبالغ فيه»، «العميل يحتاج
 *   متابعة من مدير لا مندوب». لا موعد لها ولا إنجاز، وعدّها في تقارير
 *   النشاط يجعل من يكتب كثيرًا يبدو أكثر عملًا ممّن ينفّذ.
 *
 * ولذلك جدولٌ مستقل لا نوع نشاط جديد.
 *
 * ## الصوت مرفقٌ لا عمود
 *
 * التسجيل يُرفع عبر نظام المرفقات القائم، ويُشار إليه بمعرّفه. البديل عمود
 * ثنائي في هذا الجدول يعني إعادة بناء ما بُني: حدّ الحجم، وحدّ تخزين
 * الباقة، وكشف التكرار بالبصمة، والتنزيل المحروس بالصلاحية.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   الكيانات
   ========================================================================== */

/**
 * ما يقبل تغذية راجعة.
 *
 * قائمة مغلقة كسائر الكتالوجات: بدونها يستطيع من يُرسل طلبًا مباشرًا أن
 * يعلّق على أي جدول باسمه، فيصير الجدول مساحة نصّ حرّة بلا سياق.
 */
export const FEEDBACK_ENTITIES = ['lead', 'account', 'contact', 'activity', 'task'] as const;
export type FeedbackEntity = (typeof FEEDBACK_ENTITIES)[number];

export interface FeedbackEntityDef extends Labelled<FeedbackEntity> {
  /** اسم السجل الواحد، للرسائل. */
  singular: string;
}

export const FEEDBACK_ENTITY_DEFS: readonly FeedbackEntityDef[] = [
  { key: 'lead', label: 'العملاء المحتملون', singular: 'العميل المحتمل', kind: 'info' },
  { key: 'account', label: 'العملاء', singular: 'العميل', kind: 'brand' },
  { key: 'contact', label: 'جهات الاتصال', singular: 'جهة الاتصال', kind: 'neutral' },
  { key: 'activity', label: 'الأنشطة', singular: 'النشاط', kind: 'success' },
  { key: 'task', label: 'مهام الوكالة', singular: 'المهمة', kind: 'warning' },
] as const;

export const FEEDBACK_ENTITY_MAP = Object.fromEntries(
  FEEDBACK_ENTITY_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<FeedbackEntity, FeedbackEntityDef>>;

/* ==========================================================================
   الحدود
   ========================================================================== */

/** أقصى طول للنصّ. */
export const MAX_FEEDBACK_CHARS = 4000;

/**
 * مهلة التعديل بالدقائق.
 *
 * التغذية الراجعة تُقرأ ويُبنى عليها، فتعديلها بعد أن قرأها من قرأها يغيّر
 * ما فُهم منها دون أن يعلم. والمهلة القصيرة تكفي لتصحيح خطأ إملائي أو جملة
 * أُرسلت قبل تمامها — وهو كل ما يُحتاج إليه فعلًا.
 *
 * وما عُدّل يحمل `editedAt` ويُعرض معلَّمًا: التصحيح مقبول، وإخفاؤه ليس.
 */
export const FEEDBACK_EDIT_MINUTES = 15;

/** أقصى عمق للردود. */
export const MAX_FEEDBACK_DEPTH = 1;

/* ==========================================================================
   الشكل المعاد
   ========================================================================== */

export interface FeedbackAudio {
  attachmentId: string;
  filename: string;
  /** بالبايت — يُعرض قبل التشغيل فيعرف القارئ كم سينتظر. */
  size: number;
  mimeType: string;
}

export interface FeedbackView {
  id: string;
  entity: FeedbackEntity;
  entityId: string;
  body: string | null;
  audio: FeedbackAudio | null;
  authorId: string | null;
  /** اسم الكاتب وقت الكتابة — يبقى بعد مغادرته الشركة. */
  authorName: string | null;
  /** المذكورون بمعرّفاتهم؛ تحلّها الواجهة من دليل الفريق. */
  mentions: string[];
  parentId: string | null;
  createdAt: string;
  editedAt: string | null;
  /** الردود، مسطّحةً تحت أصلها. */
  replies: FeedbackView[];
}

/**
 * هل ما زال التعديل متاحًا.
 *
 * دالة خالصة يستعملها الخادم والواجهة معًا: الواجهة تُخفي الزرّ، والخادم
 * يرفض الطلب. وإخفاءٌ بلا رفض ليس قاعدة.
 */
export function canEditFeedback(
  createdAt: string | Date,
  now: Date = new Date(),
): boolean {
  const at = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  return now.getTime() - at.getTime() <= FEEDBACK_EDIT_MINUTES * 60_000;
}

/**
 * استخراج المذكورين من النصّ.
 *
 * الصيغة `@` متبوعةً بمعرّف المستخدم، تكتبها الواجهة حين يختار من قائمة —
 * لا يكتبها المستخدم بيده. الاستخراج هنا لأن الخادم هو من يُشعر، ولا يثق
 * بقائمة تصله في الطلب: من يُرسل طلبًا مباشرًا يستطيع أن يذكر من لا يعنيه
 * الأمر فيُغرقه بإشعارات.
 */
export function extractMentions(body: string | null | undefined): string[] {
  if (!body) return [];
  const found = body.match(/@\[([a-z0-9]{20,32})\]/gi) ?? [];
  return [...new Set(found.map((m) => m.slice(2, -1)))];
}

/** يستبدل رموز الذكر بأسماء، للعرض. */
export function renderMentions(
  body: string,
  nameOf: (userId: string) => string | undefined,
): string {
  return body.replace(/@\[([a-z0-9]{20,32})\]/gi, (whole, id: string) => {
    const name = nameOf(id);
    return name ? `@${name}` : whole;
  });
}
